import React from "react";
import { TextField, Tooltip, Box } from "@mui/material";
import { useFormikContext } from "formik";

export interface GenericDateFieldProps {
  name: string;
  label: string;
  disabled?: boolean;
  placeholder?: string;
  tooltip?: string;
  required?: boolean;
  size?: "small" | "medium";
}

const GenericDateField: React.FC<GenericDateFieldProps> = ({
  name,
  label,
  disabled = false,
  placeholder,
  tooltip,
  required = false,
  size = "small",
}) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext<any>();

  // Helper function to get nested values safely
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Helper function to safely convert error to string
  const safeErrorToString = (error: any): string => {
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error !== null) {
      return Object.values(error).join(', ');
    }
    return '';
  };

  // Helper function to format date for input
  const formatDateForInput = (dateString: string | null): string => {
    if (!dateString) return "";
    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    } catch {
      return "";
    }
  };

  const fieldValue = getNestedValue(values, name);
  const fieldError = getNestedValue(errors, name);
  const fieldTouched = getNestedValue(touched, name);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Store as date string (YYYY-MM-DD) to avoid timezone issues
    setFieldValue(name, value || null);
  };

  const handleBlur = () => {
    setFieldTouched(name, true);
  };

  const dateField = (
    <TextField
      fullWidth
      size={size}
      name={name}
      label={label}
      type="date"
      value={formatDateForInput(fieldValue)}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      required={required}
      InputLabelProps={{
        shrink: true,
      }}
      error={fieldTouched && Boolean(fieldError)}
      helperText={fieldTouched && fieldError ? safeErrorToString(fieldError) : ""}
      sx={{ mb: 2 }}
    />
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="top">
      <Box>{dateField}</Box>
    </Tooltip>
  ) : (
    dateField
  );
};

export default GenericDateField;
